/**
 * Color palette logic: tier assignment, HSL darkening, opacity merging.
 */

import type { ColorPalette, NodeShape } from './types';

/**
 * Shape tier priority order:
 * 1 = process/action, 2 = decision, 3 = datastore,
 * 4 = start/end, 5 = fork/join, 6 = merge
 */
const SHAPE_TIER: Record<string, number> = {
  process: 0,
  decision: 1,
  datastore: 2,
  'start-circle': 3,
  'end-circle': 3,
  'fork-bar': 4,
  'join-bar': 4,
  'merge-diamond': 5,
  subroutine: 0,
  'class-box': 0,
  'er-entity': 0,
};

/** Parse hex color to {h, s, l} (0-360, 0-100, 0-100) */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 0, l: 0 };

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

/** Convert HSL to hex */
function hslToHex(h: number, s: number, l: number): string {
  const sNorm = s / 100;
  const lNorm = l / 100;
  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lNorm - c / 2;

  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }

  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Darken a hex color by a percentage (0–100) */
export function darken(hex: string, amount: number = 20): string {
  const { h, s, l } = hexToHsl(hex);
  return hslToHex(h, s, Math.max(0, l - amount));
}

/** Get fill and stroke colors for a shape given the palette */
export function getShapeColors(
  shape: NodeShape,
  palette: ColorPalette,
): { fillColor: string; strokeColor: string; fillOpacity: number } | null {
  if (palette.colors.length === 0) return null;

  const tier = SHAPE_TIER[shape] ?? 0;
  const idx = tier % palette.colors.length;
  const fill = palette.colors[idx];
  const stroke = darken(fill, 20);

  return {
    fillColor: fill,
    strokeColor: stroke,
    fillOpacity: Math.round(palette.opacity * 100),
  };
}

/** Merge color overrides into a draw.io style string */
export function applyColors(
  baseStyle: string,
  colors: { fillColor: string; strokeColor: string; fillOpacity: number } | null,
): string {
  if (!colors) return baseStyle;
  let style = baseStyle;
  // Remove existing color properties
  style = style.replace(/fillColor=[^;]*;?/g, '');
  style = style.replace(/strokeColor=[^;]*;?/g, '');
  style = style.replace(/fillOpacity=[^;]*;?/g, '');
  // Append new colors
  if (!style.endsWith(';')) style += ';';
  style += `fillColor=${colors.fillColor};strokeColor=${colors.strokeColor};`;
  if (colors.fillOpacity < 100) {
    style += `fillOpacity=${colors.fillOpacity};`;
  }
  return style;
}
